import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { BADGE, fmtPrice, imgUrl } from "@/lib/site";
import { Star, Zap, ShieldCheck, Package, ArrowUpRight } from "lucide-react";

const TAG_ICON = {
  "Full Access": ShieldCheck,
  "Sofort-Lieferung": Zap,
};

export const SkinCard = ({ pack, index }) => {
  const b = BADGE[pack.badge] || BADGE.premium;
  const discount =
    pack.original_price && pack.original_price > pack.price
      ? Math.round((1 - pack.price / pack.original_price) * 100)
      : 0;

  return (
    <motion.article
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-80px" }}
      transition={{ duration: 0.6, delay: (index % 3) * 0.08, ease: [0.16, 1, 0.3, 1] }}
    >
      <Link
        to={`/pack/${pack.slug}`}
        data-testid={`pack-card-${pack.slug}`}
        className="group relative border border-white/10 bg-[#0f0f11] overflow-hidden flex flex-col h-full hover:border-white/25 transition-colors"
      >
        <div className="relative aspect-[4/5] overflow-hidden">
          <img
            src={imgUrl(pack.image)}
            alt={pack.name}
            className="w-full h-full object-cover object-top transition-transform duration-[900ms] ease-out group-hover:scale-110"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0f0f11] via-transparent to-transparent opacity-90" />
          <div
            className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500"
            style={{ boxShadow: `inset 0 0 120px 10px ${b.glow}` }}
          />
          <div
            data-testid={`pack-badge-${pack.slug}`}
            className="absolute top-4 left-4 font-mono text-[10px] uppercase tracking-[0.25em] px-2.5 py-1 rounded-full border backdrop-blur-md bg-black/40"
            style={{ color: b.color, borderColor: b.color }}
          >
            {b.label}
          </div>
          {discount > 0 && (
            <div className="absolute top-4 right-4 font-mono text-[10px] tracking-[0.15em] px-2.5 py-1 rounded-full bg-[#ff0055] text-white">
              -{discount}%
            </div>
          )}
          <div className="absolute bottom-4 left-4 flex items-center gap-1 font-mono text-[11px] text-white/80 backdrop-blur-md bg-black/40 border border-white/10 px-2 py-1 rounded-full">
            <Star size={11} className="fill-[#e2ff00] text-[#e2ff00]" /> {pack.rating.toFixed(1)}
          </div>
        </div>

        <div className="p-6 md:p-7 flex flex-col flex-1">
          <h3 className="font-display font-bold text-xl md:text-2xl tracking-tight">{pack.name}</h3>
          <p className="mt-2 text-sm text-[#8b8b93] leading-relaxed">{pack.subtitle}</p>

          <div className="mt-4 flex flex-wrap gap-2">
            {pack.tags.map((t) => {
              const Icon = TAG_ICON[t] || Package;
              return (
                <span key={t} className="inline-flex items-center gap-1.5 font-mono text-[10px] tracking-[0.1em] text-[#8b8b93] border border-white/10 rounded-full px-2.5 py-1">
                  <Icon size={11} className="text-[#e2ff00]" /> {t}
                </span>
              );
            })}
          </div>

          <div className="mt-6 pt-5 border-t border-white/10 flex items-end justify-between mt-auto">
            <div>
              {pack.original_price && (
                <div className="font-mono text-xs text-[#8b8b93] line-through">{fmtPrice(pack.original_price)}</div>
              )}
              <div data-testid={`pack-price-${pack.slug}`} className="font-display font-bold text-2xl">{fmtPrice(pack.price)}</div>
            </div>
            <span
              data-testid={`pack-buy-${pack.slug}`}
              className="inline-flex items-center gap-1.5 font-mono text-xs uppercase tracking-[0.2em] bg-white text-black px-6 py-3 rounded-full group-hover:bg-[#e2ff00] transition-colors"
            >
              Ansehen <ArrowUpRight size={14} />
            </span>
          </div>
        </div>
      </Link>
    </motion.article>
  );
};
